import { skipToken } from '@reduxjs/toolkit/query'
import { ModalProps, Spin } from 'antd'
import { isEmpty } from 'lodash-es'
import { useRouter } from 'next/router'
import { FunctionComponent, RefObject } from 'react'
import { useDeepCompareEffect } from 'react-use'
import styled from 'styled-components'
import { useFetchUserStatusQuery } from '@/api/user-api'
import { useFetchPostQuery } from '@/api/workspaces-api'
import { BasicModal } from '@/components/common/antd/basic-modal'
import PostDetail from '@/components/modal/social-media-post-modal/post-detail'
import SocialMediaEmbed from '@/components/modal/social-media-post-modal/social-media-embed'
import { SafeNewTabLink } from '@/components/safe-new-tab-link'
import InstagramEmbedScript from '@/components/script/instagram-embed-script'
import { useIntl } from '@/i18n/hooks/use-intl'
import { Post } from '@/types/redux/kol-content'
import { ampli } from '@/utils/ampli'
import { convertPlatformCode, PlatformCode } from '@/utils/convert-platform'
import isNotNil from '@/utils/is-not-nil'

const SOCIAL_MEDIA_MODAL_Z_INDEX = 1031 // higher than antd Popover

export interface SocialMediaPost
  extends Pick<Post, 'crawledID' | 'postID' | 'url'> {
  platformCode: PlatformCode
  platformTitle?: string
  postType?: string
  kolId?: string
  kolDcIdHashed?: string
  kolName?: string
}

export interface SocialMediaPostModalProps
  extends Pick<ModalProps, 'open' | 'onCancel'> {
  containerRef?: RefObject<HTMLElement>
  post: SocialMediaPost
  isClickedFromDrawer?: boolean
  from?: string
}

const SocialMediaPostModal: FunctionComponent<SocialMediaPostModalProps> = ({
  open = true,
  onCancel,
  post,
  containerRef,
  isClickedFromDrawer,
  from,
}) => {
  const { formatMessage } = useIntl()
  const router = useRouter()
  const embedUrl = getEmbedUrl(post)
  const { data: userStatus } = useFetchUserStatusQuery()
  const platform = convertPlatformCode(post.platformCode)
  const { isLoading, data } = useFetchPostQuery(
    open && userStatus && isNotNil(platform?.type)
      ? {
          workspaceId: userStatus.currentWorkspaceId,
          platform_type: platform.type,
          post_id: post.postID,
        }
      : skipToken,
  )

  useDeepCompareEffect(() => {
    if (open) {
      ampli.viewSnsPost({
        kolId: post.kolId,
        kolDcId: post.kolDcIdHashed,
        kolName: post.kolName,
        path: router.asPath,
        postLink: post.url,
        postType: post.postType,
        platform: post.platformCode,
        isClickedFromDrawer,
        from,
      })
    }
  }, [open, post, isClickedFromDrawer, router.asPath])

  return (
    <>
      <InstagramEmbedScript />
      <StyledModal
        destroyOnClose
        footer={null}
        getContainer={containerRef?.current ?? document.body}
        open={open}
        zIndex={SOCIAL_MEDIA_MODAL_Z_INDEX}
        onCancel={onCancel}
      >
        <SocialMediaWrapper>
          <SocialMediaEmbedWrapper>
            <SocialMediaEmbed platformCode={post.platformCode} url={embedUrl} />
          </SocialMediaEmbedWrapper>
          <PostWrapper>
            {isLoading ? (
              <SpinWrapper>
                <Spin />
              </SpinWrapper>
            ) : (
              <PostDetail from={from} post={data} />
            )}
            {post.url && (
              <OriginalPostWrapper>
                <SafeNewTabLink
                  href={post.url}
                  onClick={(): void => {
                    ampli.viewOriginalPostFromEmbeddedPost({
                      kolId: post.kolId,
                      kolDcId: post.kolDcIdHashed,
                      kolName: post.kolName,
                      from,
                    })
                  }}
                >
                  {formatMessage({ id: 'general:view_original_post' })}
                </SafeNewTabLink>
              </OriginalPostWrapper>
            )}
          </PostWrapper>
        </SocialMediaWrapper>
      </StyledModal>
    </>
  )
}

const StyledModal = styled(BasicModal).attrs({
  width: 'fit-content',
})`
  .ant-modal-content {
    min-height: 350px;
    padding: 0;
  }
  .ant-modal-close {
    z-index: 1;
  }
`

export default SocialMediaPostModal

const SocialMediaEmbedWrapper = styled.div`
  width: fit-content;
  min-height: 350px;
  position: relative;
  margin: 0 auto;

  a {
    text-decoration: underline;
  }
`

const OriginalPostWrapper = styled.div`
  text-align: center;
  padding: 16px;

  a {
    text-decoration-line: underline;
    text-decoration-color: ${({ theme }): string => theme.colors.brand.primary};
    font-size: 16px;
  }
`

const SpinWrapper = styled.div`
  height: 350px;
  display: flex;
  justify-content: center;
  align-items: center;
`

const PostWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 436px;

  > div:first-child {
    flex: 1 1 auto;
    height: 1px;
  }

  > ${OriginalPostWrapper} {
    flex: 0 0 auto;
  }
`

const SocialMediaWrapper = styled.div`
  display: flex;
  gap: 8px;
  width: fit-content;
  position: relative;
  margin: 0 auto;
`

const getEmbedUrl = (post: SocialMediaPost): string => {
  if (post.platformCode === PlatformCode.Instagram) {
    return isEmpty(post.url)
      ? `https://www.instagram.com/p/${post.postID}`
      : post.url
  }

  if (post.platformCode === PlatformCode.Facebook) {
    return `https://www.facebook.com/${post.crawledID}/posts/${post.postID}`
  }

  if (post.platformCode === PlatformCode.YouTube) {
    return `https://www.youtube.com/watch?v=${post.postID}`
  }

  if (post.platformCode === PlatformCode.TikTok) {
    return isEmpty(post.url) ? `/video/${post.postID}` : post.url
  }
  return post.url
}
